import { DatePicker } from '@/components/DatePicker'
import { SelectedEquipmentCard } from '@/components/SelectedEquipmentCard'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { SearchableSelect, type SearchableSelectGroup } from '@/components/ui/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import type { ItemDetail } from '@/features/items/types/item'
import { useFileUploadField } from '@/hooks/useFileUpload'
import { useUI } from '@/hooks/useUI'
import { formatBuildingFloorLocation } from '@/utils/location'
import { showError } from '@/utils/toast'
import { Camera, Truck } from 'lucide-react'
import { type Dispatch, type SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { CurrentUserNote, ImageUploadField } from '../ScanItemShared'

type TransferOutForm = { targetDepartmentId: string; reason: string; scheduledDate: string; deliveryMethod: string }
type SelectedDepartmentLocation = { buildingName: string; floorName: string }

export function TransferOutSection({
  item,
  form,
  setForm,
  departmentGroups,
  selectedDepartment,
  imageField,
  currentUserName,
  isLoadingDepartments,
  isSubmitting,
  isReturningToOwnerDepartment,
  onSubmit,
}: {
  item: ItemDetail
  form: TransferOutForm
  setForm: Dispatch<SetStateAction<TransferOutForm>>
  departmentGroups: SearchableSelectGroup[]
  selectedDepartment?: SelectedDepartmentLocation
  imageField: ReturnType<typeof useFileUploadField>
  currentUserName?: string
  isLoadingDepartments: boolean
  isSubmitting: boolean
  isReturningToOwnerDepartment: boolean
  onSubmit: () => void
}) {
  const { t } = useTranslation()
  const { deliveryMethods } = useUI()
  return (
    <div className="flex flex-col gap-3" data-testid="transfer-out-form">
      <SelectedEquipmentCard
        name={item.product?.name}
        serialNumber={item.serialNumber}
        brand={item.product?.brand}
        model={item.product?.model}
        departmentName={item.department?.name}
        buildingName={item.department?.buildingName}
        floorName={item.department?.floorName}
      />
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">{t('scans.field.targetDepartment')} *</Label>
        <SearchableSelect
          value={form.targetDepartmentId}
          onValueChange={targetDepartmentId => setForm({ ...form, targetDepartmentId })}
          groups={departmentGroups}
          placeholder={t('scans.placeholder.selectDepartment')}
          searchPlaceholder={t('scans.placeholder.searchDepartment')}
          emptyText={isLoadingDepartments ? t('common.loading') : t('scans.placeholder.noDepartment')}
          triggerClassName="bg-secondary/50 text-xs"
          testId="transfer-out-department-select"
        />
        {selectedDepartment && (
          <p className="text-[10px] text-muted-foreground mt-1">
            {t('scans.transfer.selectedLocation', {
              building: formatBuildingFloorLocation(t, selectedDepartment.buildingName),
              floor: formatBuildingFloorLocation(t, null, selectedDepartment.floorName),
            })}
          </p>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('requests.scheduleDate')} *</Label>
          <DatePicker
            value={form.scheduledDate}
            onChange={scheduledDate => setForm({ ...form, scheduledDate })}
            placeholder={t('requests.chooseScheduleDate')}
            testId="transfer-out-date-input"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('requests.method')} *</Label>
          <Select value={form.deliveryMethod} onValueChange={v => setForm({ ...form, deliveryMethod: v })}>
            <SelectTrigger
              className="h-9 w-full min-w-0 bg-secondary/50 text-xs [&>span]:truncate"
              data-testid="transfer-out-delivery-method"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card text-foreground">
              {deliveryMethods?.map(method => (
                <SelectItem
                  data-testid={`transfer-out-delivery-method-option-${method.value}`}
                  key={method.value}
                  value={method.value}
                >
                  {method.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs">{t('scans.field.transferReason')} *</Label>
        <Textarea
          value={form.reason}
          onChange={e => setForm({ ...form, reason: e.target.value })}
          className="bg-secondary/50 text-xs"
          rows={2}
          placeholder={t('scans.placeholder.transferReason')}
          data-testid="transfer-out-reason"
        />
      </div>
      <ImageUploadField
        label={
          <Label className="flex items-center gap-1 text-xs">
            <Camera className="size-3" />
            {t('scans.transfer.imageBeforeTransfer')}
          </Label>
        }
        field={imageField}
        maxImages={4}
        uploadText={t('scans.uploadingImage')}
        testId="transfer-out-image-upload"
      />
      {imageField.isUploading ? (
        <p className="text-[11px] text-muted-foreground">
          {t('common.uploadingImages')}
          {imageField.uploadProgress ? ` ${imageField.uploadProgress}%` : '...'}
        </p>
      ) : null}
      <CurrentUserNote label={t('scans.transfer.sender')} name={currentUserName} />
      <Button
        data-testid="transfer-out-submit-button"
        onClick={() => (imageField.imageIds.length === 0 ? showError(t('requests.attachReceiveImage')) : onSubmit())}
        className="text-xs"
        disabled={
          !form.targetDepartmentId ||
          !form.scheduledDate ||
          !form.deliveryMethod ||
          !form.reason ||
          isLoadingDepartments ||
          imageField.isUploading ||
          isSubmitting
        }
      >
        <Truck className="mr-1 size-3" />
        {isSubmitting
          ? t('scans.transfer.submitting')
          : isReturningToOwnerDepartment
            ? t('scans.transfer.returnEquipment')
            : t('scans.transfer.transferEquipment')}
      </Button>
    </div>
  )
}
